import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from '../config/index.js';

const AuthContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

function persistSession(user, token) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

function clearSession() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, restore the cached user and re-validate the token against the API.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const cached = localStorage.getItem(USER_STORAGE_KEY);

    if (!token) {
      setLoading(false);
      return;
    }

    if (cached) {
      try {
        setUser(JSON.parse(cached));
      } catch {
        clearSession();
      }
    }

    api
      .me()
      .then((fresh) => {
        setUser(fresh);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(fresh));
      })
      .catch((err) => {
        // Only a refused token ends the session. A network failure (offline field use, or the request aborted
        // because the page is navigating away) keeps the cached session; aborting must never wipe a token
        // stored in the meantime.
        if (err?.status !== 401 && err?.status !== 403) return;
        clearSession();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { user: u, token } = await api.login({ email, password });
    persistSession(u, token);
    setUser(u);
    return u;
  }, []);

  const demoLogin = useCallback(async (email) => {
    const { user: u, token } = await api.demoLogin({ email });
    persistSession(u, token);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const value = { user, loading, isAuthenticated: !!user, login, demoLogin, logout, updateUser };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
